import { toast } from 'sonner';
import { Bell, BellRing } from 'lucide-react';
import SectionHeading from '@/components/SectionHeading';
import { Reveal, Stagger, StaggerItem } from '@/components/Reveal';
import CTABand from '@/components/CTABand';
import { useLanguage } from '@/context/LanguageContext';

const PHONE_ROUTE = 'M30 190 C60 170 75 120 105 130 S160 185 190 150 S230 70 265 95';

export default function Apps() {
  const { t } = useLanguage();
  const features = t('appsPage.features');
  return (
    <div className="pt-28" data-testid="apps-page">
      <section className="mx-auto max-w-7xl px-5 pt-8 lg:px-8">
        <SectionHeading eyebrow="appsPage.eyebrow" title="appsPage.title" sub="appsPage.sub" align="center" />
      </section>

      <section className="mx-auto grid max-w-7xl items-center gap-12 px-5 py-14 lg:grid-cols-2 lg:px-8">
        <Reveal className="flex justify-center" data-testid="apps-phone-wrap">
          <div className="relative w-64 overflow-hidden rounded-[2.6rem] border-[10px] border-brand-ink bg-white shadow-[0_50px_100px_-30px_rgba(15,23,42,0.5)]" data-testid="apps-phone-mock">
            <div className="flex items-center justify-between bg-brand-ink px-5 py-2.5">
              <span className="font-mono text-[9px] font-bold text-white" dir="ltr">WIYAKGPS</span>
              <span className="inline-flex items-center gap-1 font-mono text-[8px] uppercase tracking-widest text-brand-emerald">
                <span className="h-1.5 w-1.5 rounded-full bg-brand-emerald glow-dot" /> {t('hero.live')}
              </span>
            </div>
            <div className="relative h-56 bg-[#0B1526]" dir="ltr">
              <div className="absolute inset-0" style={{ background: 'repeating-linear-gradient(0deg, rgba(125,180,255,0.08) 0 1px, transparent 1px 28px), repeating-linear-gradient(90deg, rgba(125,180,255,0.08) 0 1px, transparent 1px 28px)' }} />
              <svg viewBox="0 0 300 220" className="h-full w-full">
                <path d={PHONE_ROUTE} fill="none" stroke="#38BDF8" strokeWidth="3" strokeLinecap="round" strokeDasharray="8 7" style={{ animation: 'dash-move 1.3s linear infinite' }} />
                <circle r="6" fill="#0052FF" stroke="#fff" strokeWidth="2">
                  <animateMotion dur="5s" repeatCount="indefinite" path={PHONE_ROUTE} />
                </circle>
                <g transform="translate(60 70)"><circle r="4" fill="#10B981" /><text x="8" y="3" fill="#94A3B8" style={{ font: '600 9px JetBrains Mono, monospace' }}>TR-118</text></g>
                <g transform="translate(210 60)"><circle r="4" fill="#10B981" /><text x="8" y="3" fill="#94A3B8" style={{ font: '600 9px JetBrains Mono, monospace' }}>VN-207</text></g>
              </svg>
            </div>
            <div className="space-y-2 p-3.5">
              <div className="flex items-center gap-2 rounded-xl border border-brand-amber/30 bg-brand-amber/10 px-3 py-2" data-testid="apps-phone-alert">
                <Bell className="h-3.5 w-3.5 text-brand-amber" />
                <span className="font-mono text-[9px] font-semibold text-amber-700" dir="ltr">120 km/h · TR-204</span>
              </div>
              <div className="flex items-center justify-between rounded-xl bg-brand-paper px-3 py-2">
                <span className="font-mono text-[9px] font-semibold text-brand-steel" dir="ltr">TR-204</span>
                <span className="font-mono text-[9px] font-bold text-brand-blue" dir="ltr">104 km/h</span>
              </div>
              <div className="grid grid-cols-4 gap-1.5 pt-1">
                {[0, 1, 2, 3].map((i) => (
                  <span key={i} className={`h-1.5 rounded-full ${i === 0 ? 'bg-brand-blue' : 'bg-brand-line'}`} />
                ))}
              </div>
            </div>
          </div>
        </Reveal>

        <div>
          <Reveal>
            <h2 className="font-display text-3xl font-extrabold tracking-tight text-brand-ink" data-testid="apps-phone-title">{t('appsPage.phoneTitle')}</h2>
            <p className="mt-4 leading-relaxed text-brand-steel">{t('appsPage.phoneDesc')}</p>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="mt-6 flex items-start gap-4 rounded-2xl border border-brand-blue/25 bg-brand-blue/5 p-5" data-testid="apps-alerts-card">
              <span className="grid h-11 w-11 shrink-0 place-items-center rounded-xl bg-brand-blue text-white"><BellRing className="h-5 w-5" /></span>
              <div>
                <h3 className="font-display text-base font-bold text-brand-ink">{t('appsPage.alertsTitle')}</h3>
                <p className="mt-1 text-sm text-brand-steel">{t('appsPage.alertsDesc')}</p>
              </div>
            </div>
          </Reveal>
          <Reveal delay={0.18} className="mt-6 flex flex-wrap gap-3">
            {['android', 'ios'].map((k) => (
              <button
                key={k}
                data-testid={`apps-page-badge-${k}`}
                onClick={() => toast.info(t(`download.badges.${k === 'android' ? 'android' : 'ios'}`), { description: 'WiyakGPS · v4.2.1' })}
                className="inline-flex items-center gap-2 rounded-full bg-brand-ink px-6 py-3 text-sm font-bold text-white transition-opacity hover:opacity-90"
              >
                {t(`download.badges.${k === 'android' ? 'android' : 'ios'}`)}
              </button>
            ))}
          </Reveal>
        </div>
      </section>

      <section className="border-t border-brand-line bg-white py-20" data-testid="apps-features">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <Stagger className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3" gap={0.08}>
            {features.map((f, i) => (
              <StaggerItem key={f.t}>
                <div className="card-hover h-full rounded-2xl border border-brand-line bg-brand-paper p-6" data-testid={`apps-feature-${i}`}>
                  <span className="font-mono text-[10px] font-bold uppercase tracking-widest text-brand-blue" dir="ltr">0{i + 1}</span>
                  <h3 className="mt-2.5 font-display text-base font-bold text-brand-ink">{f.t}</h3>
                  <p className="mt-1.5 text-sm leading-relaxed text-brand-steel">{f.d}</p>
                </div>
              </StaggerItem>
            ))}
          </Stagger>
        </div>
      </section>

      <CTABand />
    </div>
  );
}
